import { Link } from 'react-router-dom';
import { Utensils, ChefHat, ArrowRight } from 'lucide-react';
import { useDossierContext } from './SuiviPrivatisationLayout';
import { COLLECTE_STATUS_LABELS } from '../Evenementiel/collecteStatus';
import { PIPELINE_STAGE_LABELS, hasPassedPivot } from './pipelineStages';

/**
 * Onglet « Menus & Jour J » — la partie du parcours qui appartient à d'autres modules.
 *
 * Rien n'est recalculé ici : le statut de la collecte est celui de Menus & Convives, lu tel quel depuis son
 * registre (`collecteStatus.ts`), et le Jour J renvoie vers le Mode terrain. Redupliquer les douze statuts
 * de la collecte aurait garanti qu'ils divergent au premier changement.
 */
export function DossierMenusPage() {
    const { dossier } = useDossierContext();
    // Sans privatisation dans Événementiel, ni la collecte ni le Mode terrain n'ont d'objet : tous deux
    // travaillent sur un `event_id`. C'est vrai jusqu'à « Privatisation créée », pas seulement jusqu'à la
    // signature — d'où les deux conditions distinctes.
    const signe = hasPassedPivot(dossier.stage);
    const eventId = dossier.event?.id || null;

    return (
        <div className="space-y-3">
            <div className="rounded-xl border border-border p-3">
                <p className="text-xs font-bold text-content flex items-center gap-1.5">
                    <Utensils size={13} /> Collecte des menus
                </p>
                {!signe ? (
                    // Le message dit la RÈGLE, pas seulement l'interdiction : « ce n'est pas encore possible »
                    // sans expliquer pourquoi se lit comme une panne, et fait ouvrir un ticket.
                    <p className="text-[11px] text-content-muted mt-1.5 leading-relaxed">
                        La collecte démarre à la signature du devis. Ce dossier en est au stade
                        « {PIPELINE_STAGE_LABELS[dossier.stage]} » — on ne fait pas choisir son plat à un
                        convive pour un événement qui n'est pas encore vendu.
                    </p>
                ) : !eventId ? (
                    <p className="text-[11px] text-content-muted mt-1.5 leading-relaxed">
                        Le devis est signé : créez la privatisation depuis le bouton en haut de page pour lui
                        donner sa date. La collecte se lancera ensuite.
                    </p>
                ) : dossier.collecte_status ? (
                    <div className="mt-1.5 flex items-center gap-2 flex-wrap">
                        <span className="text-[10px] font-black uppercase px-2 py-1 rounded bg-brand-500/10 text-brand-600 dark:text-brand-400 border border-brand-500/20">
                            {COLLECTE_STATUS_LABELS[dossier.collecte_status] || dossier.collecte_status}
                        </span>
                        <Link to={`/menus-convives/${eventId}`}
                            className="text-[11px] font-bold text-brand-600 dark:text-brand-400 hover:underline">
                            Ouvrir Menus &amp; Convives
                        </Link>
                    </div>
                ) : (
                    <p className="text-[11px] text-content-muted mt-1.5">
                        Le devis est signé : la collecte peut être lancée depuis le bouton en haut de page.
                    </p>
                )}
            </div>

            <div className="rounded-xl border border-border p-3">
                <p className="text-xs font-bold text-content flex items-center gap-1.5">
                    <ChefHat size={13} /> Jour J
                </p>
                <p className="text-[11px] text-content-muted mt-1.5">
                    L'organisation du service se pilote depuis le Mode terrain, dans le module Cuisine — c'est
                    lui qui clôt le dossier avec « Terminer le service ».
                </p>
                {eventId ? (
                    <Link to={`/mode-terrain/${eventId}`}
                        className="inline-flex items-center gap-1.5 mt-2 text-[11px] font-bold text-brand-600 dark:text-brand-400 hover:underline">
                        Ouvrir le Mode terrain <ArrowRight size={11} />
                    </Link>
                ) : (
                    <p className="text-[10px] text-content-muted mt-1 italic">
                        Disponible une fois la privatisation créée.
                    </p>
                )}
            </div>
        </div>
    );
}
